/**
 * Migration 001 — userStatus number → "system:N" string.
 *
 * v1 stored userStatus as a plain number (enum 1–14).
 * v2 uses string format: "system:3" for InProgress, etc.
 * The runtime guard `normalizeUserStatus()` handles both at read time,
 * but this migration cleans up the stored data.
 *
 * Idempotent: only writes objects where `typeof userStatus === "number"`.
 */

import { db } from "@/db/schema"
import type { DataMigration } from "./migrationTypes"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BATCH_SIZE = 500
const YIELD_MS = 50

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

export const userStatusToStringMigration: DataMigration = {
  id: "001-userStatus-to-string",
  description:
    "Convert userStatus from v1 number format to v2 string format (system:N)",
  priority: 5,

  async needsMigration(): Promise<boolean> {
    // Sample up to 10 objects with a numeric userStatus
    const sample = await db.totodo
      .where("userStatus")
      .above(0)
      .limit(10)
      .toArray()

    return sample.some((obj) => typeof obj.userStatus === "number")
  },

  async execute(): Promise<number> {
    let totalMigrated = 0

    // Process in batches to avoid blocking
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Find next batch of objects with numeric userStatus
      const batch = await db.totodo
        .where("userStatus")
        .above(0)
        .limit(BATCH_SIZE)
        .toArray()

      // Filter to only truly numeric values (not strings that happen to be indexed)
      const toMigrate = batch.filter(
        (obj) => typeof obj.userStatus === "number",
      )

      if (toMigrate.length === 0) break

      // Bulk update — convert number → "system:N"
      const updates = toMigrate.map((obj) => ({
        key: obj.id as string,
        changes: {
          userStatus: `system:${obj.userStatus}`,
        },
      }))

      await db.totodo.bulkUpdate(updates)
      totalMigrated += updates.length

      // Yield to main thread between batches
      if (toMigrate.length === BATCH_SIZE) {
        await new Promise((resolve) => setTimeout(resolve, YIELD_MS))
      } else {
        break // Last batch was smaller than BATCH_SIZE — done
      }
    }

    return totalMigrated
  },
}
